import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Cpu,
  Headset,
  HelpCircle,
  Mail,
  Quote,
  RefreshCw,
  ShieldCheck,
  Truck,
  Wrench,
  Zap,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import useEmblaCarousel from "embla-carousel-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import { openWhatsApp } from "@/components/WhatsAppLeadModal";
import { useCountUp, useRevealOnScroll } from "@/hooks/useReveal";
import { useIsMobile } from "@/hooks/useMobile";
import { ASSETS,
  CTA_TEXT,
  EMAIL,
  INSTALLATION_PHOTOS,
  setPageSeo,
  SLOGAN,
} from "@/lib/brand";
import { STORAGE_MEDIA_ORIGIN } from "@/lib/mediaRecovery";

/* ---------------------------------- Hero ---------------------------------- */

const HERO_SLIDES = [
  {
    id: 1,
    image: `https://equipapro-zblaoftc.manus.space/manus-storage/hero-slide1-prime-v4_51615f9c_d6510470.webp`,
    imageAlt: "Porta giratória Prime EquipaPRO preta na entrada de edifício premium noturno — tambor preto, vidro curvo, interior iluminado em âmbar",
    eyebrow: "REFERÊNCIA EM CONTROLE DE ACESSO",
    title: "Portas de segurança e controle de acesso de alto padrão",
    subtitle: "Inteligência em cada porta. Confiança em cada acesso.",
    description: "Da segurança de bancos, cooperativas e órgãos públicos à arquitetura de alto padrão de hotéis e empreendimentos, fabricamos portas e controle de acesso com engenharia própria, tecnologia integrada e acabamento impecável.",
    cta: { label: "Conhecer produtos", href: "/produtos" },
    gradient: "from-charcoal/90 via-charcoal/55 to-charcoal/10",
  },
  {
    id: 2,
    image: `https://equipapro-zblaoftc.manus.space/manus-storage/hero-slide2-laranja-v5_ad30af78_f2194435.webp`,
    imageAlt: "Mapa do Brasil com áreas de atendimento EquipaPRO em todo o território nacional",
    eyebrow: "COBERTURA NACIONAL",
    title: "Atendemos todo o Brasil.",
    subtitle: "Tecnologia EquiSense — exclusiva EquipaPRO",
    description: "Presença em todas as regiões do país: Sul, Sudeste, Centro-Oeste, Nordeste e Norte. Nossos produtos chegam até você com suporte técnico especializado e a maior garantia do setor.",
    cta: { label: "Ver áreas de atendimento", href: "/contato" },
    gradient: "from-charcoal/90 via-charcoal/45 to-transparent",
    mobileObjectPosition: "70% center",
    mobileScale: "scale-75",
  },
  {
    id: 3,
    image: "https://equipapro-zblaoftc.manus.space/manus-storage/svc-manutencao-preventiva_9837ebe1_ea4cb3e4.jpg",
    imageAlt: "Técnico EquipaPRO treinado para manutenção preventiva e corretiva de portas automáticas",
    eyebrow: "SUPORTE E MANUTENÇÃO",
    title: "Profissionais treinados para manutenção preventiva e corretiva.",
    subtitle: "Seu equipamento sempre funcionando.",
    description: "Equipe técnica especializada para manutenção preventiva, corretiva e contratos de suporte. Atendimento rápido, peças originais e relatório técnico em cada visita.",
    cta: { label: "Solicitar manutenção", href: "#", onCtaClick: () => openWhatsApp({ message: 'Olá! Gostaria de solicitar manutenção de porta automática.', channel: 'suporte', origin: 'hero-slide-manutencao' }) },
    gradient: "from-charcoal/90 via-charcoal/55 to-charcoal/10",
    mobileObjectPosition: "85% center",
  },
  {
    id: 4,
    image: `https://equipapro-zblaoftc.manus.space/manus-storage/hero-slide4-fachada-moderna_329492e9_f8324252.webp`,
    imageAlt: "Fachada corporativa moderna com porta automática e jardim — projeto EquipaPRO",
    eyebrow: "+1.400 PROJETOS ENTREGUES",
    title: "Pensamos em cada detalhe do seu projeto.",
    subtitle: "Engenharia própria. Acabamento impecável.",
    description: "Mais de uma década entregando projetos de alto padrão para bancos, cooperativas, hospitais e indústrias. Cada instalação com tecnologia integrada, peças originais e suporte especializado.",
    cta: { label: "Ver nossos projetos", href: "/sobre" },
    gradient: "from-charcoal/90 via-charcoal/55 to-charcoal/20",
    mobileObjectPosition: "60% center",
  },
] as const;

function Hero() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = HERO_SLIDES.length;
  const isMobile = useIsMobile();

  // Autoplay
  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % total);
    }, 6000);
    return () => clearInterval(timer);
  }, [paused, total]);

  const prev = () => setCurrent((c) => (c - 1 + total) % total);
  const next = () => setCurrent((c) => (c + 1) % total);

  const slide = HERO_SLIDES[current];

  return (
    <section
      id="home"
      className="relative overflow-hidden bg-charcoal pt-16"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carrossel"
      aria-label="Destaques EquipaPRO"
    >
      {/* Slides de fundo com transição */}
      {HERO_SLIDES.map((s, i) => (
        <div
          key={s.id}
          className="absolute inset-0 transition-opacity duration-700"
          style={{ opacity: i === current ? 1 : 0, zIndex: 0 }}
          aria-hidden={i !== current}
        >
          {/* Slide 3 no mobile: fundo sólido charcoal sem imagem do técnico */}
          {!(isMobile && s.id === 3) && (
            <img
              src={s.image}
              alt={s.imageAlt}
              width={1920}
              height={1080}
              fetchPriority={i === 0 ? "high" : undefined}
              loading={i === 0 ? "eager" : "lazy"}
              className="h-full w-full object-cover opacity-100 brightness-125 contrast-110 saturate-110"
              style={{
                objectPosition: isMobile
                  ? s.id === 2 ? '70% center'
                  : 'center'
                  : 'center'
              }}
            />
          )}
          {/* Overlay base para garantir contraste do texto */}
          <div
            className="absolute inset-0 bg-black/10"
            aria-hidden="true"
          />
          <div
            className={`absolute inset-0 bg-gradient-to-r ${s.gradient}`}
            aria-hidden="true"
          />
        </div>
      ))}

      {/* Conteúdo do slide ativo */}
      <div className="container relative z-10 grid items-center py-24 md:py-32 lg:min-h-[640px] lg:grid-cols-[1.1fr_0.9fr]">
        <div className="max-w-2xl">
          <p className="eyebrow">{slide.eyebrow}</p>
          <h1
            key={`title-${current}`}
            className="mt-4 text-3xl leading-tight text-white sm:text-4xl lg:text-[2.9rem] lg:leading-[1.12] animate-fade-in"
          >
            {slide.title}
          </h1>
          <p className="mt-5 max-w-xl text-lg font-medium text-warmgold">
            {slide.subtitle}
          </p>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-white/70">
            {slide.description}
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => openWhatsApp({
                message: "Olá! Quero falar com a EquipaPRO sobre uma solução para o meu projeto.",
                channel: "comercial",
                origin: `home-hero-slide-${slide.id}`,
              })}
              className="btn-press inline-flex items-center gap-2 rounded-md bg-warmgold px-7 py-4 text-base font-semibold text-white shadow-lg shadow-warmgold/20 hover:bg-warmgold/90"
            >
              {CTA_TEXT}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
            {'onCtaClick' in slide.cta && slide.cta.onCtaClick ? (
              <button
                type="button"
                onClick={slide.cta.onCtaClick}
                className="btn-press inline-flex items-center gap-2 rounded-md border border-white/40 bg-white/10 px-7 py-4 text-base font-semibold text-white hover:border-white/70 hover:bg-white/20">
                {slide.cta.label}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            ) : (
              <a
                href={slide.cta.href}
                className="btn-press inline-flex items-center gap-2 rounded-md border border-white/40 bg-white/10 px-7 py-4 text-base font-semibold text-white hover:border-white/70 hover:bg-white/20">
                {slide.cta.label}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Setas de navegação — ocultas no mobile para não cobrir o texto */}
      <button
        onClick={prev}
        aria-label="Slide anterior"
        className="hidden md:flex absolute left-6 top-1/2 z-20 -translate-y-1/2 rounded-full border border-white/20 bg-black/30 p-2.5 text-white backdrop-blur-sm transition hover:bg-black/50"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        onClick={next}
        aria-label="Próximo slide"
        className="hidden md:flex absolute right-6 top-1/2 z-20 -translate-y-1/2 rounded-full border border-white/20 bg-black/30 p-2.5 text-white backdrop-blur-sm transition hover:bg-black/50"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      {/* Bolinhas indicadoras */}
      <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 gap-2" role="tablist" aria-label="Slides">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.id}
            role="tab"
            aria-selected={i === current}
            aria-label={`Ir para slide ${i + 1}`}
            onClick={() => setCurrent(i)}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === current
                ? "w-6 bg-warmgold"
                : "w-2 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>
    </section>
  );
}

/* ------------------------------ Stats (numbers) ---------------------------- */

function Stat({
  value,
  prefix = "",
  suffix = "",
  label,
  isNumeric = true,
  display,
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  label: string;
  isNumeric?: boolean;
  display?: string;
}) {
  const { ref, value: counted } = useCountUp(value);
  return (
    <div className="reveal flex flex-col items-center text-center">
      <span
        ref={ref}
        className={`font-display text-warmgold ${
          isNumeric
            ? "text-4xl sm:text-5xl"
            : "text-2xl leading-[1.6] sm:text-3xl sm:leading-[1.7]"
        }`}>
        {isNumeric
          ? `${prefix}${counted.toLocaleString("pt-BR")}${suffix}`
          : display}
      </span>
      <span className="mt-2 text-sm font-medium text-offwhite/75">{label}</span>
    </div>
  );
}

function Stats() {
  return (
    <section aria-label="Números da EquipaPRO" className="premium-dark">
      <div className="container grid grid-cols-2 items-end gap-x-6 gap-y-10 py-14 md:grid-cols-4 md:py-16">
        <Stat value={10} prefix="+" label="Anos no mercado" />
        <Stat value={1400} prefix="+" label="Projetos entregues" />
        <Stat value={100} suffix="%" label="Fabricação própria" />
        <div className="reveal flex flex-col items-center text-center">
          <img
            src={ASSETS.brazilMap}
            alt="Mapa do Brasil com malha de conexões — atuação da EquipaPRO em todo o território nacional e no exterior"
            width={500}
            height={500}
            loading="lazy"
            className="h-24 w-24 object-contain sm:h-28 sm:w-28"
          />
          <span className="mt-2 text-sm font-medium text-offwhite/75">
            Atuação nacional e internacional
          </span>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- O que fazemos ----------------------------- */

const ECOSYSTEM = [
  {
    image: `https://equipapro-zblaoftc.manus.space/manus-storage/home-product-1_dc75bccd_f98f363e.png`,
    title: "Porta Automática",
    text: "Equipamento físico fabricado com engenharia própria",
  },
  {
    image: `https://equipapro-zblaoftc.manus.space/manus-storage/home-product-2_5321fbfc_95476d85.png`,
    title: "Porta Giratória",
    text: "Detecção de metais e bloqueio com fabricação própria",
  },
  {
    image: `https://equipapro-zblaoftc.manus.space/manus-storage/home-product-3_89ac0fe7_764b402c.png`,
    title: "Controle de acesso",
    text: "Liberação inteligente por regra, credencial e horário",
  },
  {
    image: `https://equipapro-zblaoftc.manus.space/manus-storage/home-product-4_59358ad0_1d317de0.png`,
    title: "Inteligência Biométrica",
    text: "Reconhecimento facial integrado à passagem",
  },
  {
    image: `https://equipapro-zblaoftc.manus.space/manus-storage/home-product-5_ef04e64b_1f9d7c45.png`,
    title: "Câmera",
    text: "CFTV e monitoramento nativos ao equipamento",
  },
  {
    image: `https://equipapro-zblaoftc.manus.space/manus-storage/home-product-6_e1895583_d3459d64.png`,
    title: "Automação",
    text: "Eletrônica e software desenvolvidos internamente",
  },
] as {
  icon?: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
    "aria-hidden"?: boolean | "true" | "false";
  }>;
  image?: string;
  title: string;
  text: string;
}[];

function WhatWeDo() {
  const trackRef = useRef<HTMLDivElement>(null);

  function scrollEcosystem(dir: "prev" | "next") {
    const wrapper = trackRef.current;
    if (!wrapper) return;
    // Temporarily disable animation so manual scroll works
    const track = wrapper.querySelector(".ecosystem-marquee-track") as HTMLElement | null;
    if (!track) return;
    const cardW = 160 + 16; // card width + gap
    const delta = dir === "next" ? cardW * 2 : -cardW * 2;
    // Shift the animation offset by adjusting translateX via a CSS variable
    const currentOffset = parseFloat(track.style.getPropertyValue("--scroll-offset") || "0");
    const newOffset = currentOffset + delta;
    track.style.setProperty("--scroll-offset", `${newOffset}px`);
    track.style.transform = `translateX(${newOffset}px)`;
    // Resume after 600ms
    track.style.animationPlayState = "paused";
    setTimeout(() => {
      track.style.animationPlayState = "";
      track.style.transform = "";
      track.style.removeProperty("--scroll-offset");
    }, 800);
  }

  return (
    <section id="sobre" className="bg-softgray/40">
      <div className="container py-16 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow reveal">O que fazemos</p>
          <h2 className="reveal mt-3 text-2xl text-charcoal sm:text-3xl">
            Integração real, da fabricação à programação de cada detalhe.
          </h2>
          <p className="reveal mt-5 text-base leading-relaxed text-stonegray">
            A EquipaPRO fabrica e integra portas automáticas, portas
            giratórias, detectores de metais, eclusas, catracas, torniquetes,
            controle de acesso e biometria — em um só ecossistema, com
            engenharia e software próprios. Nossas soluções são projetadas em
            conformidade com as normas técnicas ABNT aplicáveis, com
            equipamentos de segurança homologados e suporte especializado do
            projeto à manutenção preventiva e corretiva.
          </p>
        </div>

        <div className="relative mt-12">
          {/* Seta esquerda */}
          <button
            onClick={() => scrollEcosystem("prev")}
            aria-label="Anterior"
            className="absolute -left-4 top-1/2 z-10 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full border border-charcoal/10 bg-white shadow-sm transition hover:bg-warmgold hover:text-white hover:border-warmgold"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <div className="ecosystem-marquee-wrapper" ref={trackRef}>
          <div className="ecosystem-marquee-track">
            {[...ECOSYSTEM, ...ECOSYSTEM].map((item, i) => (
              <div
                key={`${item.title}-${i}`}
                className="ecosystem-marquee-card flex flex-col items-center rounded-lg border border-charcoal/8 bg-white p-6 text-center shadow-[0_1px_2px_rgba(26,26,26,0.04)]">
                {item.image ? (
                  <img
                    src={item.image}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    width={36}
                    height={36}
                    className="h-9 w-9 object-contain"
                  />
                ) : item.icon ? (
                  <item.icon
                    className="h-9 w-9 p-1 text-warmgold"
                    strokeWidth={1.6}
                    aria-hidden="true"
                  />
                ) : null}
                <h3 className="mt-3 font-sans text-sm font-semibold text-charcoal">
                  {item.title}
                </h3>
                <p className="mt-1.5 text-xs leading-relaxed text-stonegray">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
          </div>

          {/* Seta direita */}
          <button
            onClick={() => scrollEcosystem("next")}
            aria-label="Próximo"
            className="absolute -right-4 top-1/2 z-10 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full border border-charcoal/10 bg-white shadow-sm transition hover:bg-warmgold hover:text-white hover:border-warmgold"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <p className="reveal mx-auto mt-10 max-w-xl text-center text-sm text-stonegray">
          <span className="font-semibold text-charcoal">
            Acesso integrado é na EquipaPRO!
          </span>
        </p>
      </div>
    </section>
  );
}

/* --------------------------------- Produtos -------------------------------- */

const PRODUCT_GROUPS = [
  {
    image: `https://equipapro-zblaoftc.manus.space/manus-storage/prod-porta-automatica_a8992f48_2fea1a8d.png`,
    title: "Portas Automáticas",
    text: "Deslizantes, telescópicas, pivotantes, giratórias e curvas, com tecnologia anti-esmagamento EquiSense.",
    href: "/produtos?categoria=portas-automaticas",
    badges: ["equisense", "iot"],
  },
  {
    image: `https://equipapro-zblaoftc.manus.space/manus-storage/prod-eclusa_3df312bd_899c172f.png`,
    title: "Eclusas de Segurança",
    text: "Entrada individual com portas intertravadas e detecção de metais, incluindo versões acessíveis e com inspeção remota.",
    href: "/produtos?categoria=eclusas-de-seguranca",
    badges: ["iot"],
  },
  {
    image: `https://equipapro-zblaoftc.manus.space/manus-storage/prod-detector-metais_514af319_0aca30b4.png`,
    title: "Detectores de Metais",
    text: "Detecção precisa de armas e objetos metálicos, em versões fixas e portáteis para eventos e indústrias.",
    href: "/produtos?categoria=detectores-e-inspecao",
    badges: ["iot"],
  },
  {
    image: `https://equipapro-zblaoftc.manus.space/manus-storage/prod-catraca_44592b11_05e71794.png`,
    title: "Controle de Acesso",
    text: "Catracas e torniquetes com biometria facial, cartão, QR Code e registro completo de acessos.",
    href: "/produtos?categoria=controle-de-acesso",
    badges: ["iot"],
  },
  {
    image: `https://equipapro-zblaoftc.manus.space/manus-storage/prod-porta-hermetica_c7d2dd46_a7e2d783.png`,
    title: "Portas Herméticas Hospitalares",
    text: "Vedação eficiente para centros cirúrgicos, UTIs e salas limpas, em conformidade com a RDC 50/2002.",
    href: "/produtos?categoria=portas-hermeticas-hospitalares",
    badges: ["equisense"],
  },
  {
    image: `https://equipapro-zblaoftc.manus.space/manus-storage/prod-porta-blindada_85506523_b3225fb9.png`,
    title: "Cofres e Blindagens",
    text: "Linha própria de cofres, cabines blindadas e portas blindadas de alto padrão com marca EquipaPRO.",
    href: "/produtos?categoria=cofres-e-blindagens",
    badges: [],
  },
  {
    image: `https://equipapro-zblaoftc.manus.space/manus-storage/prod-porta-giratoria-social_4aef7697_15f9348e.png`,
    title: "Porta Giratória Social",
    text: "Porta giratória de vidro para entradas corporativas e comerciais de alto padrão, com design premium.",
    href: "/produtos?categoria=portas-automaticas",
    badges: ["equisense", "iot"],
  },
  {
    image: `https://equipapro-zblaoftc.manus.space/manus-storage/prod-porta-giratoria-seg_c40f48c2_03759402.png`,
    title: "Porta Giratória com Detector de Metais",
    text: "Porta giratória com detector de metais integrado para bancos, cooperativas e órgãos públicos.",
    href: "/produtos?categoria=portas-automaticas",
    badges: ["iot"],
  },
] as {
  image?: string;
  title: string;
  text: string;
  href: string;
  badges?: string[];
}[];

function Products() {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 1,
  });
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    return () => { emblaApi.off("select", onSelect); };
  }, [emblaApi]);

  return (
    <section id="produtos" className="bg-softgray">
      <div className="container py-20 md:py-24">
        <div className="max-w-2xl">
          <p className="eyebrow reveal">Produtos</p>
          <h2 className="reveal mt-3 text-2xl text-charcoal sm:text-3xl">
            Soluções completas em portas e controle de acesso
          </h2>
          <p className="reveal mt-4 text-base leading-relaxed text-stonegray">
            Equipamentos completos, com tecnologia embarcada. Projetos sob
            medida, com instalação e suporte técnico em todo o Brasil.
          </p>
        </div>

        {/* Carrossel Embla — mesmo padrão do InstallationCarousel */}
        <div className="reveal relative mt-12">
          <div className="overflow-hidden rounded-xl" ref={emblaRef}>
            <div className="flex gap-4">
              {PRODUCT_GROUPS.map((group) => (
                <div
                  key={group.title}
                  className="min-w-0 flex-[0_0_260px] sm:flex-[0_0_300px]">
                  <div className="group flex h-full flex-col overflow-hidden rounded-xl border border-charcoal/8 bg-white shadow-[0_1px_2px_rgba(26,26,26,0.04)] transition-shadow hover:shadow-md">
                    {/* Imagem vertical do produto */}
                    <div className="relative overflow-hidden" style={{ aspectRatio: "3/4" }}>
                      {group.image ? (
                        <img
                          src={group.image}
                          alt={group.title}
                          loading="lazy"
                          width={300}
                          height={400}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-softgray px-6 text-center">
                          <ShieldCheck className="h-10 w-10 text-warmgold/60" strokeWidth={1.3} aria-hidden="true" />
                          <span className="text-xs font-semibold uppercase tracking-wide text-charcoal/50">Imagem em atualização</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-charcoal/0 transition-colors duration-300 group-hover:bg-charcoal/20" />
                    </div>
                    {/* Conteúdo textual */}
                    <div className="flex flex-1 flex-col p-5">
                      <h3 className="font-sans text-base font-semibold text-charcoal">{group.title}</h3>
                      <p className="mt-1.5 flex-1 text-sm leading-relaxed text-stonegray">{group.text}</p>
                      {group.badges && group.badges.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {group.badges.includes("equisense") && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-warmgold/30 bg-warmgold/8 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-warmgold">
                              <ShieldCheck className="h-2.5 w-2.5" aria-hidden="true" />
                              Tecnologia EquiSense
                            </span>
                          )}
                          {group.badges.includes("iot") && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-blue-400/30 bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-500">
                              <Cpu className="h-2.5 w-2.5" aria-hidden="true" />
                              IoT Equic0r3x
                            </span>
                          )}
                        </div>
                      )}
                      <div className="mt-4 flex flex-col gap-2">
                        <a
                          href={group.href}
                          className="btn-press inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-charcoal/15 bg-softgray px-4 py-2 text-sm font-semibold text-charcoal transition-colors hover:bg-charcoal/8">
                          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                          Saiba mais
                        </a>
                        <button
                          type="button"
                          onClick={() => openWhatsApp({ message: `Olá! Tenho interesse em ${group.title}. Gostaria de solicitar um orçamento.`, channel: 'comercial', origin: `produto-${group.href}` })}
                          className="btn-press inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-warmgold px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-warmgold/20 transition-colors hover:bg-warmgold/90">
                          Solicitar Orçamento
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Setas sobrepostas — mesmo estilo do InstallationCarousel */}
          <button
            type="button"
            aria-label="Produtos anteriores"
            onClick={() => emblaApi?.scrollPrev()}
            className="absolute left-3 top-[40%] flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-charcoal shadow-md transition-transform duration-150 ease-out hover:bg-white active:scale-95">
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label="Próximos produtos"
            onClick={() => emblaApi?.scrollNext()}
            className="absolute right-3 top-[40%] flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-charcoal shadow-md transition-transform duration-150 ease-out hover:bg-white active:scale-95">
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </button>

          {/* Bolinhas indicadoras */}
          <div className="mt-4 flex justify-center gap-2" role="tablist" aria-label="Navegar produtos">
            {PRODUCT_GROUPS.map((group, i) => (
              <button
                key={group.title}
                type="button"
                role="tab"
                aria-selected={selected === i}
                aria-label={`Produto ${i + 1}`}
                onClick={() => emblaApi?.scrollTo(i)}
                className={`h-2 rounded-full transition-all duration-200 ease-out ${
                  selected === i ? "w-6 bg-warmgold" : "w-2 bg-charcoal/20 hover:bg-charcoal/35"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="reveal mt-10 flex flex-col items-center gap-5">
          <a
            href="/produtos"
            className="btn-press inline-flex items-center gap-2 rounded-md bg-charcoal px-7 py-3.5 text-base font-semibold text-white hover:bg-charcoal/90">
            Ver todos os produtos
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
          <button
            type="button"
            onClick={() => openWhatsApp({ message: 'Olá! Vim pelo site da EquipaPRO e gostaria de falar sobre um projeto.', channel: 'comercial', origin: 'home-produtos-cta' })}
            className="inline-flex items-center gap-2 text-sm font-semibold text-warmgold hover:underline">
            Fale com um especialista sobre o seu projeto
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── Tecnologias Exclusivas ─────────────────────────────── */

function TechExclusive() {
  return (
    <section
      id="tecnologias"
      className="relative overflow-hidden bg-charcoal"
      aria-labelledby="h-tech-exclusive"
    >
      {/* Textura de fundo sutil */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 50%, #E8A020 0%, transparent 55%), radial-gradient(circle at 80% 30%, #E8A020 0%, transparent 50%)",
        }}
        aria-hidden="true"
      />

      <div className="container relative py-20 md:py-24">
        {/* Cabeçalho */}
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow reveal" style={{ color: "#E8A020" }}>
            Inovação Própria
          </p>
          <h2
            id="h-tech-exclusive"
            className="reveal mt-3 text-2xl text-white sm:text-3xl"
          >
            Tecnologias exclusivas que nenhum concorrente tem
          </h2>
          <p className="reveal mt-4 text-base leading-relaxed text-white/60">
            Desenvolvemos nossas próprias tecnologias — do hardware ao software.
            É o que nos permite entregar integração real, não apenas promessas.
          </p>
        </div>

        {/* Cards das tecnologias */}
        <div className="reveal mt-14 grid gap-6 md:grid-cols-2">
          {/* EquiSense */}
          <div className="group relative flex flex-col rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm transition-all duration-300 hover:border-warmgold/40 hover:bg-white/8">
            {/* Badge de tecnologia exclusiva */}
            <div className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-warmgold/30 bg-warmgold/10 px-3.5 py-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-warmgold" aria-hidden="true" />
              <span className="text-xs font-bold uppercase tracking-widest text-warmgold">
                Tecnologia Exclusiva · Registro Requerido
              </span>
            </div>

            {/* Ícone grande */}
            <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-xl bg-warmgold/15 ring-1 ring-warmgold/20">
              <ShieldCheck className="h-7 w-7 text-warmgold" aria-hidden="true" />
            </div>

            <h3 className="font-display text-2xl font-bold text-white">
              Equi<span className="text-warmgold">Sense</span>
            </h3>
            <p className="mt-1 text-sm font-semibold uppercase tracking-widest text-white/40">
              Tecnologia Antiesmagamento
            </p>

            <p className="mt-4 flex-1 text-base leading-relaxed text-white/70">
              Enquanto a maioria das portas automáticas do mercado ainda
              apresenta risco de esmagamento, o EquiSense detecta a presença e
              interrompe o movimento antes do contato — protegendo pessoas sem
              abrir mão da segurança do patrimônio.
            </p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              {[
                { icon: ShieldCheck, label: "Detecção de presença" },
                { icon: Zap, label: "Interrupção instantânea" },
                { icon: ShieldCheck, label: "Sem risco de contato" },
                { icon: ShieldCheck, label: "Tecnologia com registro" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2">
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-warmgold/20">
                    <Icon className="h-3 w-3 text-warmgold" aria-hidden="true" />
                  </div>
                  <span className="text-xs text-white/60">{label}</span>
                </div>
              ))}
            </div>

            {/* Selo de garantia 3 anos */}
            <div className="mt-6 flex items-center gap-4 rounded-xl border border-warmgold/20 bg-warmgold/5 p-4">
              <img
                src={`https://equipapro-zblaoftc.manus.space/manus-storage/selo-3anos-v2_ddbcbdd4_b4bfbddf.png`}
                alt="Selo de 3 anos de garantia"
                width={80}
                height={80}
                loading="lazy"
                className="h-20 w-20 shrink-0 object-contain drop-shadow-lg"
              />
              <div>
                <p className="text-base font-bold text-warmgold">3 anos de garantia</p>
                <p className="mt-1 text-sm text-white/60">Portas automáticas com tecnologia EquiSense</p>
              </div>
            </div>
          </div>

          {/* Equic0r3x */}
          <div className="group relative flex flex-col rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm transition-all duration-300 hover:border-blue-400/40 hover:bg-white/8">
            {/* Badge IoT */}
            <div className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-blue-400/30 bg-blue-400/10 px-3.5 py-1.5">
              <Cpu className="h-3.5 w-3.5 text-blue-400" aria-hidden="true" />
              <span className="text-xs font-bold uppercase tracking-widest text-blue-400">
                Plataforma IoT · Desenvolvimento Próprio
              </span>
            </div>

            {/* Ícone grande */}
            <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-xl bg-blue-400/15 ring-1 ring-blue-400/20">
              <Cpu className="h-7 w-7 text-blue-400" aria-hidden="true" />
            </div>

            <h3 className="font-display text-2xl font-bold text-white">
              Equic<span className="text-blue-400">0</span>r3x
            </h3>
            <p className="mt-1 text-sm font-semibold uppercase tracking-widest text-white/40">
              Placa Eletrônica IoT
            </p>

            <p className="mt-4 flex-1 text-base leading-relaxed text-white/70">
              O cérebro dos nossos equipamentos: placa eletrônica IoT com
              processamento avançado, baixo consumo e eficiência energética.
              Customizável para cada projeto, integrável com outras tecnologias
              — inclusive rodando detecção de metais.
            </p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              {[
                { icon: Cpu, label: "Processamento avançado" },
                { icon: Zap, label: "Baixo consumo" },
                { icon: Cpu, label: "Customizável por projeto" },
                { icon: Cpu, label: "Detecção de metais" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2">
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-400/20">
                    <Icon className="h-3 w-3 text-blue-400" aria-hidden="true" />
                  </div>
                  <span className="text-xs text-white/60">{label}</span>
                </div>
              ))}
            </div>

            {/* Selo de garantia 2 anos */}
            <div className="mt-6 flex items-center gap-4 rounded-xl border border-blue-400/20 bg-blue-400/5 p-4">
              <img
                src={`https://equipapro-zblaoftc.manus.space/manus-storage/selo-2anos-v2_0d16bb7e_1f769213.png`}
                alt="Selo de 2 anos de garantia"
                width={80}
                height={80}
                loading="lazy"
                className="h-20 w-20 shrink-0 object-contain drop-shadow-lg"
              />
              <div>
                <p className="text-base font-bold text-blue-400">2 anos de garantia</p>
                <p className="mt-1 text-sm text-white/60">Equipamentos de segurança com Equic0r3x</p>
              </div>
            </div>
          </div>
        </div>

        {/* Rodapé da seção */}
        <div className="reveal mt-10 flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-white/40">
            Tecnologias desenvolvidas.
          </p>
          <a
            href="/tecnologias"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-warmgold hover:underline"
          >
            Conheça nossas tecnologias
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Nossos Serviços ---------------------------- */

const SERVICES = [
  {
    icon: Wrench,
    title: "Manutenção",
    text: "Preventiva, corretiva e contratos de manutenção com atendimento prioritário — planos que prolongam a vida útil do equipamento e evitam paradas.",
    tags: ["Preventiva", "Corretiva", "Contrato de manutenção"],
  },
  {
    icon: Truck,
    title: "Instalação e Remanejo",
    text: "Equipe própria para instalação completa de equipamentos novos e remanejo de portas e sistemas já existentes para novos endereços ou layouts.",
    tags: ["Instalação completa", "Remanejo de equipamentos"],
  },
  {
    icon: RefreshCw,
    title: "Retrofit",
    text: "Modernização de equipamentos antigos — inclusive de outras marcas — com automação, eletrônica e tecnologia EquipaPRO atualizadas.",
    tags: ["Modernização", "Multimarcas"],
  },
  {
    icon: Headset,
    title: "Suporte Técnico Remoto",
    text: "Diagnóstico e resolução à distância com engenharia própria, reduzindo tempo de resposta e custos de deslocamento.",
    tags: ["Diagnóstico remoto", "Resposta rápida"],
  },
] as const;

function Services() {
  return (
    <section id="servicos" className="bg-white">
      <div className="container py-20 md:py-24">
        <div className="max-w-2xl">
          <p className="eyebrow reveal">Nossos serviços</p>
          <h2 className="reveal mt-3 text-2xl text-charcoal sm:text-3xl">
            Do projeto à operação, cuidamos de todo o ciclo de vida
          </h2>
          <p className="reveal mt-4 text-base leading-relaxed text-stonegray">
            Quem fabrica é quem melhor mantém. Nossa equipe técnica atende em
            todo o Brasil com peças originais e engenharia própria.
          </p>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((service, i) => (
            <article
              key={service.title}
              className="reveal group flex flex-col rounded-lg border border-charcoal/8 bg-softgray/60 p-7 transition-shadow hover:shadow-md"
              style={{ transitionDelay: `${i * 60}ms` }}>
              <service.icon
                className="h-8 w-8 text-warmgold"
                strokeWidth={1.6}
                aria-hidden="true"
              />
              <h3 className="mt-4 font-sans text-lg font-semibold text-charcoal">
                {service.title}
              </h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-stonegray">
                {service.text}
              </p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {service.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-warmgold/30 bg-warmgold/8 px-2.5 py-1 text-xs font-medium text-warmgold">
                    {tag}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>

        <div className="reveal mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => openWhatsApp({ message: 'Olá! Vim pelo site da EquipaPRO e gostaria de falar sobre serviços de manutenção, instalação, retrofit ou suporte técnico.', channel: 'suporte', origin: 'home-servicos-cta' })}
            className="inline-flex items-center gap-2 text-sm font-semibold text-warmgold hover:underline">
            Solicitar atendimento técnico
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- Prova social ------------------------------ */

const CLIENTS = [
  { name: "Caixa", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-caixa_8d7df26e_23c869e7.png` },
  { name: "Banco do Brasil", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-bancodobrasil_22e0d80b_3adb6125.png` },
  { name: "Petrobras", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-petrobras_280cf9f8_6b5fa2eb.png` },
  { name: "Safra", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-safra_d5fe716d_f9bb69fd.png` },
  { name: "Banco do Nordeste", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-bancodonordeste_afb5261d_aa82cac3.png` },
  { name: "Sicredi", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-sicredi_dbe2f306_19020e7b.png` },
  { name: "Sicoob", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-sicoob_aa0ac5f7_7892c03d.png` },
  { name: "Cresol", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-cresol_08902958_c22f4f39.png` },
  { name: "Unicred", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-unicred_630042ea_db0bbdaf.png` },
  { name: "Ailos", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-ailos_9cb967a8_0dfabcbf.png` },
  { name: "Prosegur", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-prosegur_a4b116ae_4d287e7f.png` },
  { name: "Grupo GPS", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-grupogps_a78a5165_ab2d8c3c.png` },
  { name: "Grupo SC", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-gruposc_2c20fffc_d88cf8f6.png` },
  { name: "CAU/RS", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-caurs_fe77ec55_67289391.png` },
  { name: "Coco Bambu", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-cocobambu_f21add58_f5d5bf55.png` },
  { name: "PJRO", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-pjro_d114eecd_52a92ba9.png` },
  { name: "TJ-PI", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-client-44_57e9db4d_1b1215a8.png` },
  { name: "MPPR", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-client-45_a50fd584_d5b544e0.png` },
  { name: "TJGO", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-client-46_858840c9_5b006fba.png` },
  { name: "Portonovo", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-client-47_cd883f87_ddbd9864.png` },
  { name: "Frigotil", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-client-48_45319d4f_6e16c6b2.png` },
  { name: "Sicoob Metalcred", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-client-49_fe7de261_af0d161b.png` },
  { name: "Gran Parrilla", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-client-50_a2253af0_ce8c21a1.png` },
  { name: "Uniprime", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-client-51_e18aec06_d23cdea2.png` },
  { name: "Hipper Freios", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-client-52_c72c08e9_80e9e990.png` },
  { name: "Brasal Refrigerantes", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-client-53_22eb204b_9339f2ee.png` },
  { name: "Gontijo", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-client-54_5b56c9ac_260399f4.png` },
  { name: "Aura Apoena", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-client-55_e3a0a49f_4b03a7f6.png` },
  { name: "Prefeitura Canabrava do Norte", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-client-56_999d9460_eaa1a45e.png` },
  { name: "Gran Food", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-client-57_0079ac4f_3cafa684.png` },
  { name: "Oktoberfest Santo Cristo", logo: `https://equipapro-zblaoftc.manus.space/manus-storage/logo-client-58_13a4371d_08bd0b82.png` },
];

function SocialProof() {
  return (
    <section aria-label="Clientes e prova social" className="bg-white">
      <div className="container py-20 md:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow reveal">Confiança comprovada</p>
          <h2 className="reveal mt-3 text-2xl text-charcoal sm:text-3xl">
            +1.400 projetos entregues
          </h2>
          <p className="reveal mt-4 text-base leading-relaxed text-stonegray">
            Instituições que exigem o mais alto padrão de segurança confiam na
            EquipaPRO.
          </p>
        </div>

        {/* Duas fileiras de logos em marquee — velocidades opostas para efeito dinâmico */}
        <div className="logo-marquee-2rows reveal mt-12" role="region" aria-label="Logos de clientes">
          {/* Linha 1: logos 0-7 — rola para a esquerda */}
          <ul className="logo-marquee-track-row logo-row-left">
            {[...CLIENTS.slice(0, 8), ...CLIENTS.slice(0, 8)].map((client, i) => (
              <li
                key={`row1-${client.name}-${i}`}
                aria-hidden={i >= 8}
                className="logo-card-item group flex w-44 shrink-0 items-center justify-center rounded-lg border border-charcoal/8 bg-white px-5 py-4 sm:w-52">
                <img src={client.logo} alt={i >= 8 ? "" : `Logo ${client.name}`} width={180} height={72} loading="lazy" className="h-12 w-full object-contain" />
              </li>
            ))}
          </ul>
          {/* Linha 2: logos 8-15 — rola para a direita (direção oposta) */}
          <ul className="logo-marquee-track-row logo-row-right mt-3">
            {[...CLIENTS.slice(8), ...CLIENTS.slice(8)].map((client, i) => (
              <li
                key={`row2-${client.name}-${i}`}
                aria-hidden={i >= CLIENTS.slice(8).length}
                className="logo-card-item group flex w-44 shrink-0 items-center justify-center rounded-lg border border-charcoal/8 bg-white px-5 py-4 sm:w-52">
                <img src={client.logo} alt={i >= CLIENTS.slice(8).length ? "" : `Logo ${client.name}`} width={180} height={72} loading="lazy" className="h-12 w-full object-contain" />
              </li>
            ))}
          </ul>
        </div>

        {/* Título acima do carrossel de projetos */}
        <div className="reveal mt-16 mb-2 text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-warmgold">Projetos EquipaPRO</p>
          <h2 className="mt-2 font-display text-2xl font-bold text-charcoal md:text-3xl">
            Conheça alguns dos nossos projetos realizados
          </h2>
          <p className="mt-2 text-sm text-stonegray">Instalações entregues em todo o Brasil — bancos, cooperativas, hospitais, indústrias e eventos.</p>
        </div>
        <InstallationCarousel />
      </div>
    </section>
  );
}

/** Carrossel de instalações — para adicionar fotos, basta incluir em INSTALLATION_PHOTOS (brand.ts). */
function InstallationCarousel() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  return (
    <div className="reveal relative mx-auto mt-12 max-w-3xl">
      <div className="overflow-hidden rounded-lg shadow-lg" ref={emblaRef}>
        <div className="flex">
          {INSTALLATION_PHOTOS.map((photo) => (
            <figure key={photo.src} className="relative min-w-0 flex-[0_0_100%]">
              <img
                src={photo.src}
                alt={photo.alt}
                width={1600}
                height={899}
                loading="lazy"
                className="aspect-[16/9] w-full object-cover"
              />
              <figcaption className="absolute bottom-3 left-3 rounded bg-charcoal/80 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm">
                {photo.caption}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>

      <button
        type="button"
        aria-label="Foto anterior"
        onClick={() => emblaApi?.scrollPrev()}
        className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-charcoal shadow-md transition-transform duration-150 ease-out hover:bg-white active:scale-95">
        <ChevronLeft className="h-5 w-5" aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label="Próxima foto"
        onClick={() => emblaApi?.scrollNext()}
        className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-charcoal shadow-md transition-transform duration-150 ease-out hover:bg-white active:scale-95">
        <ChevronRight className="h-5 w-5" aria-hidden="true" />
      </button>

      <div className="mt-4 flex justify-center gap-2" role="tablist" aria-label="Selecionar foto">
        {INSTALLATION_PHOTOS.map((photo, i) => (
          <button
            key={photo.src}
            type="button"
            role="tab"
            aria-selected={selected === i}
            aria-label={`Foto ${i + 1}`}
            onClick={() => emblaApi?.scrollTo(i)}
            className={`h-2 rounded-full transition-all duration-200 ease-out ${
              selected === i ? "w-6 bg-warmgold" : "w-2 bg-charcoal/20 hover:bg-charcoal/35"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------- Depoimentos (WhatsApp) -------------------- */

type Testimonial = {
  message: string;
  sender: string;
  role: string;
  time: string;
  photo: string;
};

/** Depoimentos reais de clientes, recuperados da versão anterior com seus arquivos originais. */
const TESTIMONIALS: Testimonial[] = [
  {
    message: "O melhor equipamento que já trabalhamos. Não dá manutenção e a equipe adorou. É o que a gente precisa no dia a dia 👏",
    sender: "Engenheiro Sênior",
    role: "Instituição Financeira Federal",
    time: "10:42",
    photo: `https://equipapro-zblaoftc.manus.space/manus-storage/testimonial-1_7c2a985b_09a1e192.jpg`,
  },
  {
    message: "Prefiro comprar da EquipaPRO. É um dos melhores equipamentos do mercado — qualidade que justifica cada centavo.",
    sender: "Analista de Compras",
    role: "Cooperativa de Crédito · Sul do Brasil",
    time: "14:17",
    photo: `https://equipapro-zblaoftc.manus.space/manus-storage/testimonial-2_679d6123_ffcacec1.jpg`,
  },
  {
    message: "Compramos pela qualidade e durabilidade. Já testamos outras marcas, mas voltamos sempre para a EquipaPRO ✅",
    sender: "Gestor de Patrimônio",
    role: "Cooperativa de Crédito · Santa Catarina",
    time: "09:05",
    photo: `https://equipapro-zblaoftc.manus.space/manus-storage/testimonial-3_3e26eb55_b446423c.jpg`,
  },
  {
    message: "Design clean e minimalista, mas com toda a qualidade e segurança que precisamos. Combina perfeitamente com nossa identidade.",
    sender: "Diretor Administrativo",
    role: "Cooperativa de Crédito · Região Norte",
    time: "16:30",
    photo: `https://equipapro-zblaoftc.manus.space/manus-storage/testimonial-4_ac214d78_22742eb0.jpg`,
  },
  {
    message: "Imagina, obrigada vocês pelo pronto atendimento de sempre 🙏 A equipe é incrível, resolve tudo rápido e com muito cuidado.",
    sender: "Gestora de Segurança",
    role: "Cooperativa de Crédito · Sul do Brasil",
    time: "11:58",
    photo: `https://equipapro-zblaoftc.manus.space/manus-storage/testimonial-5_eb79622d_9dbb8759.jpg`,
  },
];

/** Checkmark duplo azul (lido) — SVG inline fiel ao WhatsApp */
function DoubleCheck() {
  return (
    <svg
      viewBox="0 0 18 11"
      width="18"
      height="11"
      aria-hidden="true"
      className="inline-block shrink-0"
    >
      <path
        d="M17.394 1.557L8.31 10.64a.75.75 0 01-1.06 0L3.56 6.95a.75.75 0 011.06-1.06l3.16 3.16 8.554-8.554a.75.75 0 011.06 1.06z"
        fill="#53bdeb"
      />
      <path
        d="M11.894 1.557L6.81 6.64 5.56 5.39a.75.75 0 00-1.06 1.06l1.78 1.78a.75.75 0 001.06 0l5.614-5.613a.75.75 0 00-1.06-1.06z"
        fill="#53bdeb"
      />
    </svg>
  );
}

/** Ícone WhatsApp SVG */
function WhatsAppIcon({ size = 20 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path fill="#25d366" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

function TestimonialCard({ t }: { t: Testimonial }) {
  return (
    <article
      className="overflow-hidden rounded-2xl shadow-xl"
      style={{ background: "#fff" }}
      aria-label={`Depoimento de ${t.sender}`}
    >
      {/* Retrato recuperado da publicação anterior, associado ao depoimento original. */}
      <div className="flex items-center gap-4 px-6 pt-6 pb-4">
        <img
          src={t.photo}
          alt={`Foto de ${t.sender}`}
          width={56}
          height={56}
          loading="lazy"
          className="h-14 w-14 rounded-full object-cover shadow-md ring-2 ring-white"
        />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900 text-base leading-tight">{t.sender}</p>
          <p className="text-sm text-gray-500 mt-0.5">{t.role}</p>
        </div>
        <div className="shrink-0 opacity-80">
          <WhatsAppIcon size={22} />
        </div>
      </div>

      {/* Separador */}
      <div className="mx-6 h-px bg-gray-100" />

      {/* Área de mensagem estilo WhatsApp */}
      <div
        className="px-5 py-5"
        style={{ background: "#e5ddd5" }}
      >
        {/* Balão de mensagem recebida */}
        <div
          className="relative max-w-[90%] rounded-tr-2xl rounded-br-2xl rounded-bl-2xl px-4 py-3 text-sm leading-relaxed"
          style={{
            background: "#fff",
            color: "#111b21",
            boxShadow: "0 1px 3px rgba(0,0,0,.15)",
          }}
        >
          {/* Orelha do balão */}
          <span
            className="absolute -left-2 top-0 h-4 w-2 overflow-hidden"
            aria-hidden="true"
            style={{
              background: "radial-gradient(circle at 0 0, transparent 12px, #fff 12px)",
            }}
          />
          <p className="text-gray-800">{t.message}</p>
          {/* Hora + tique */}
          <span
            className="ml-2 inline-flex items-center gap-1 float-right mt-1.5"
            style={{ color: "#8696a0", fontSize: "0.68rem" }}
          >
            {t.time}
            <DoubleCheck />
          </span>
        </div>
      </div>
    </article>
  );
}

function Testimonials() {
  if (TESTIMONIALS.length === 0) return null;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 1,
    breakpoints: {
      "(min-width: 768px)": { slidesToScroll: 1 },
    },
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => { emblaApi.off("select", onSelect); };
  }, [emblaApi]);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();

  return (
    <section aria-label="Depoimentos de clientes" className="bg-softgray/60 overflow-hidden">
      <div className="container py-20 md:py-24">
        {/* Cabeçalho */}
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow reveal" style={{ color: "#25d366" }}>
            Depoimentos
          </p>
          <h2 className="reveal mt-3 text-2xl text-charcoal sm:text-3xl">
            O que nossos clientes falam da gente
          </h2>
          <p className="reveal mt-3 text-sm text-charcoal/50">
            Mensagens reais, publicadas com autorização e identidade preservada.
          </p>
        </div>

        {/* Carrossel */}
        <div className="mt-12 relative">
          <div ref={emblaRef} className="overflow-hidden">
            <div className="flex gap-5">
              {TESTIMONIALS.map((t, i) => (
                <div
                  key={t.sender + i}
                  className="min-w-0 shrink-0 grow-0 basis-full sm:basis-[calc(50%-10px)] lg:basis-[calc(33.333%-14px)]"
                >
                  <TestimonialCard t={t} />
                </div>
              ))}
            </div>
          </div>

          {/* Navegação */}
          <div className="mt-8 flex items-center justify-center gap-4">
            <button
              onClick={scrollPrev}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-charcoal/20 bg-white shadow-sm transition hover:bg-charcoal hover:text-white"
              aria-label="Depoimento anterior"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            {/* Dots */}
            <div className="flex gap-2">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => emblaApi?.scrollTo(i)}
                  className="h-2 rounded-full transition-all"
                  style={{
                    width: selectedIndex === i ? "24px" : "8px",
                    background: selectedIndex === i ? "#1a6b3c" : "#d1d5db",
                  }}
                  aria-label={`Ir para depoimento ${i + 1}`}
                />
              ))}
            </div>

            <button
              onClick={scrollNext}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-charcoal/20 bg-white shadow-sm transition hover:bg-charcoal hover:text-white"
              aria-label="Próximo depoimento"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- Blog Preview ---------------------------- */

function BlogPreview() {
  const { data: posts, isLoading, isError } = trpc.blog.listPosts.useQuery();
  const recent = posts?.slice(0, 3) ?? [];

  // Don't render section if no posts and not loading
  if (!isLoading && !isError && recent.length === 0) return null;

  return (
    <section id="blog-preview" className="bg-softgray/40">
      <div className="container py-20 md:py-24">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="eyebrow reveal">Blog &amp; Insights</p>
            <h2 className="reveal mt-3 text-2xl text-charcoal sm:text-3xl">
              Conteúdo técnico para quem decide
            </h2>
          </div>
          <a
            href="/blog"
            className="reveal inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-warmgold hover:underline">
            Ver todos os artigos
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading && Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex flex-col overflow-hidden rounded-xl border border-charcoal/8 bg-white">
              <div className="aspect-video w-full animate-pulse bg-charcoal/8" />
              <div className="flex flex-col gap-3 p-6">
                <div className="h-4 w-3/4 animate-pulse rounded bg-charcoal/8" />
                <div className="h-3 w-full animate-pulse rounded bg-charcoal/5" />
                <div className="h-3 w-2/3 animate-pulse rounded bg-charcoal/5" />
              </div>
            </div>
          ))}
          {isError && (
            <p className="col-span-3 text-center text-sm text-stonegray">Não foi possível carregar os artigos.</p>
          )}
          {!isLoading && !isError && recent.map((post, i) => (
            <a
              key={post.slug}
              href={`/blog/${post.slug}`}
              className="reveal group flex flex-col overflow-hidden rounded-xl border border-charcoal/8 bg-white transition-shadow hover:shadow-md"
              style={{ transitionDelay: `${i * 60}ms` }}>
              {post.coverImageUrl ? (
                <img
                  src={post.coverImageUrl}
                  alt={post.title}
                  width={640}
                  height={360}
                  loading="lazy"
                  className="aspect-video w-full object-cover"
                />
              ) : (
                <div className="aspect-video w-full bg-charcoal/5" />
              )}
              <div className="flex flex-1 flex-col p-6">
                <h3 className="font-display text-base font-semibold text-charcoal group-hover:text-warmgold transition-colors line-clamp-2">
                  {post.title}
                </h3>
                {post.excerpt && (
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-stonegray line-clamp-3">
                    {post.excerpt}
                  </p>
                )}
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-warmgold">
                  Ler artigo <ArrowRight className="h-3 w-3" aria-hidden="true" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- CTA final ------------------------------- */

function IntegradorCTA() {
  return (
    <section aria-label="CTA Programa de Parceiros" className="bg-[#1a6b3c]">
      <div className="container py-12 md:py-14">
        <div className="flex flex-col items-center justify-between gap-6 text-center md:flex-row md:text-left">
          <div className="max-w-xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">Programa de Parceiros</p>
            <h2 className="mt-2 text-xl font-bold text-white sm:text-2xl">
              Quer integrar nossas tecnologias nos seus projetos?
            </h2>
            <p className="mt-2 text-sm text-white/75">
              Integradores, representantes e distribuidores têm acesso a condições exclusivas, suporte técnico dedicado e treinamento da nossa equipe.
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
            <a
              href="/parceiros"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-6 py-3 text-sm font-semibold text-[#1a6b3c] shadow transition hover:bg-white/90 active:scale-[0.97]"
            >
              Seja um Integrador
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
            <button
              type="button"
              onClick={() => openWhatsApp({ message: 'Olá! Gostaria de saber mais sobre as soluções da EquipaPRO.', channel: 'comercial', origin: 'home-cta-section' })}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/30 bg-white/10 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/20 active:scale-[0.97]"
            >
              Fale pelo WhatsApp
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section id="contato" className="premium-dark">
      <div className="container flex flex-col items-center py-20 text-center md:py-24">
        <img
          src={ASSETS.monogramEP}
          alt="Monograma EP da EquipaPRO"
          width={158}
          height={110}
          loading="lazy"
          className="h-12 w-auto opacity-90"
        />
        <h2 className="reveal mt-6 max-w-2xl text-2xl text-offwhite sm:text-3xl">
          Seu projeto merece um parceiro que fabrica, integra e acompanha
        </h2>
        <p className="reveal mt-4 max-w-xl text-base leading-relaxed text-offwhite/65">
          Fale agora com o nosso time comercial e receba uma proposta sob medida
          para a sua operação.
        </p>
        <div className="reveal mt-8 flex flex-col items-center gap-4 sm:flex-row">
          <button
            type="button"
            onClick={() => openWhatsApp({ message: 'Olá! Gostaria de receber uma proposta da EquipaPRO.', channel: 'comercial', origin: 'home-final-cta' })}
            className="btn-press inline-flex items-center gap-2 rounded-md bg-warmgold px-8 py-4 text-base font-semibold text-offwhite hover:bg-warmgold/90">
            {CTA_TEXT}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
          <a
            href={`mailto:${EMAIL}`}
            className="inline-flex items-center gap-2 text-sm font-medium text-offwhite/75 hover:text-warmgold">
            <Mail className="h-4 w-4" aria-hidden="true" />
            {EMAIL}
          </a>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------- HomeFAQ ---------------------------------- */

const HOME_FAQ = [
  {
    q: "Como solicito um orçamento?",
    a: "Preencha o formulário na página de Contato ou fale pelo WhatsApp (48) 99158-3576. Nossa equipe responde em até 1 dia útil com proposta técnica personalizada.",
  },
  {
    q: "A EquipaPRO fabrica os próprios equipamentos?",
    a: "Sim. Temos parque fabril próprio em Garopaba/SC com mais de 1.000 m², onde fabricamos portas automáticas, giratórias, eclusas, portais, torniquetes, catracas e blindagens.",
  },
  {
    q: "Vocês atendem em todo o Brasil?",
    a: "Sim. Atendemos projetos em todo o território nacional e também no exterior, com equipe técnica para instalação e comissionamento em qualquer estado.",
  },
  {
    q: "Quais certificações os produtos possuem?",
    a: "Nossos produtos são certificados pela UNISUL, possuem laudo eletromagnético (LABCERT), conformidade com normas IEC e ABNT, e são RoHS Compliant — livres de substâncias perigosas.",
  },
];

function HomeFAQ() {
  return (
    <section className="bg-softgray/50 py-16 lg:py-20" aria-labelledby="h-home-faq">
      <div className="container">
        <div className="mb-10 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-warmgold">
              <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
              Dúvidas frequentes
            </span>
            <h2 id="h-home-faq" className="reveal mt-2 text-2xl font-bold text-charcoal sm:text-3xl">
              Perguntas frequentes
            </h2>
          </div>
          <a
            href="/faq"
            className="group inline-flex items-center gap-1.5 text-sm font-medium text-warmgold transition-colors hover:text-warmgold/70"
          >
            Ver todas as perguntas
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
          </a>
        </div>

        <div className="mx-auto max-w-3xl">
          <Accordion type="single" collapsible className="w-full">
            {HOME_FAQ.map((item, i) => (
              <AccordionItem key={i} value={`home-faq-${i}`} className="border-charcoal/10">
                <AccordionTrigger className="text-left text-base font-semibold text-charcoal hover:text-warmgold">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-charcoal/70">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------- Page ---------------------------------- */

export default function Home() {
  useRevealOnScroll();
  useLayoutEffect(() => {
    setPageSeo({
      title: "Controle de Acesso e Portas de Segurança para Empresas | EquipaPRO",
      description: "Soluções de portas de segurança, automação e controle de acesso para gestores de segurança, facilities, engenharia e operações em todo o Brasil.",
      path: "/",
    });
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ overflowX: "clip" }}>
      <Navbar />
      <main>
        <Hero />
        <Stats />
        <WhatWeDo />
        <SocialProof />
        <Products />
        <TechExclusive />
        <Services />
        <section id="blog" aria-hidden="true" className="h-0 overflow-hidden" />
        <BlogPreview />
        <Testimonials />
        <HomeFAQ />
        <IntegradorCTA />
        <FinalCTA />
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
